/**
 * IPQC Service Layer - New Simplified API
 *
 * This service layer handles all IPQC API calls using the new simplified `/ipqc/*` endpoints.
 * It replaces the legacy `/ipqc/v3/*` endpoints.
 *
 * Key changes from V3:
 * - Flat stage-based structure instead of nested machine/process hierarchy
 * - Readings submitted individually or in batch by sheetId + parameterId
 * - Resamples submitted via child reading arrays in the same readings payload
 * - Handover driven by session end flow
 */

import { api, isNetworkUnavailableError } from "@/services/base"
import {
  dedupeOfflineSyncSheets,
  dedupeProductSchemas,
  dedupeProductsWithForms,
} from "@/lib/offline-dedupe"
import {
  FormSchema,
  HandoverFormSchema,
  HandoverProductSchema,
  InternalProcessSchema,
  MachineSchema,
  ParamChoice,
  ParamGradeLabel,
  ParamGradeRange,
  ParamSchema,
  ParamSchemaLimit,
  ParamSchemaMode,
  ParamSchemaType,
  ParamValidationType,
  ParamWindow,
  ProductionLineSchema,
  ProductSchema,
} from "@/lib/form/schema"
import { BreakdownFormData, FormParamStatus } from "@/lib/form/types"
import { createUuid, EnumsOf } from "@/lib/utils"
import { HTTPError } from "ky"
import useStore from "@/store"
import { toLineStatusKey, toLineStatusKeyParts } from "@/store/ipqc"

// ============================================================================
// API Types - Direct mapping from backend API responses
// ============================================================================

export const ParameterType = {
  CHOICE: "CHOICE",
  VALUE_TEXT: "VALUE_TEXT",
  VALUE_NUMBER: "VALUE_NUMBER",
  DATE: "DATE",
  RADIO: "RADIO",
  DROPDOWN: "DROPDOWN",
} as const
export type ParameterType = EnumsOf<typeof ParameterType>

export const ParameterValidationType = {
  LIMIT: "LIMIT",
  CHOICE: "CHOICE",
  FLAG: "FLAG",
  NONE: "NONE",
} as const
export type ParameterValidationType = EnumsOf<typeof ParameterValidationType>

export const ParameterMode = {
  INFO: "INFO",
  TEST: "TEST",
} as const
export type ParameterMode = EnumsOf<typeof ParameterMode>

export const ReadingStatus = {
  PENDING: "PENDING",
  FILLED_COMPLETE: "FILLED_COMPLETE",
  FILLED_PENDING_APPROVAL: "FILLED_PENDING_APPROVAL",
  REJECTED: "REJECTED",
  RESAMPLED: "RESAMPLED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  DRAFT: "DRAFT",
} as const
export type ReadingStatus = EnumsOf<typeof ReadingStatus>

export const SheetStatus = {
  NEW: "NEW",
  DRAFT: "DRAFT",
  DELETED: "DELETED",
  SUBMITTED: "SUBMITTED",
  COMPLETED: "COMPLETED",
  HANDOVER_NEW: "HANDOVER_NEW",
  HANDOVER_DRAFT: "HANDOVER_DRAFT",
  HANDOVER_SUBMITTED: "HANDOVER_SUBMITTED",
  HANDOVER_COMPLETED: "HANDOVER_COMPLETED",
} as const
export type SheetStatus = EnumsOf<typeof SheetStatus>

export const SessionStatus = {
  STARTED: "STARTED",
  DRAFT: "DRAFT",
  ENDED: "ENDED",
  DELETED: "DELETED",
} as const
export type SessionStatus = EnumsOf<typeof SessionStatus>

export const MachineBreakDownType = {
  BREAK_DOWN: "BREAK_DOWN",
  POWER_FAILURE: "POWER_FAILURE",
  CHANGE_OVER: "CHANGE_OVER",
  OTHER: "OTHER",
} as const
export type MachineBreakDownType = EnumsOf<typeof MachineBreakDownType>

export const StageType = {
  MACHINE: "MACHINE",
  PROCESS: "PROCESS",
} as const
export type StageType = EnumsOf<typeof StageType>

// Parameter configuration types
export interface ParameterChoiceResponse {
  value: string
  label: string
  isCorrect: boolean
}

export interface ParameterGradeRangeResponse {
  minValue: number
  maxValue: number
  label: string
  color?: string
}

export interface FormulaInputResponse {
  variable: string
  name: string
  unit?: string | null
}

export interface FormulaFactorsResponse {
  [key: string]: number
}

export interface ParameterResponse {
  id: string
  name: string
  mode: ParameterMode
  type: ParameterType
  validationType: ParameterValidationType
  unit?: string | null
  description?: string | null
  mandatory: boolean
  sequenceOrder: number
  minLimit?: number | null
  maxLimit?: number | null
  choices?: ParameterChoiceResponse[] | null
  intervalMinutes?: number | null
  formulaText?: string | null
  formulaInputs?: FormulaInputResponse[] | null
  formulaFactors?: FormulaFactorsResponse | null
  gradeRanges?: ParameterGradeRangeResponse[] | null
}

// Reading types
export interface WindowReadingResponse {
  id: string
  startTime: string
  endTime: string
  value: string
  gradeLabel?: string | null
  status: ReadingStatus
  isFormula: boolean
  remark?: string | null
  recordedAt?: string | null
  sequenceOrder?: number | null
  overtime?: boolean | null
  early?: boolean | null
  childWindows?: WindowReadingResponse[]
}

export interface ReadingResponse {
  id: string
  parameterId: string
  parameterName?: string
  value: string
  gradeLabel?: string | null
  status: ReadingStatus
  isFormula: boolean
  remark?: string | null
  recordedAt?: string | null
  sequenceOrder?: number | null
  windowReadings?: WindowReadingResponse[]
  childReadings?: ReadingResponse[]
}

// Breakdown types
export interface BreakdownResponse {
  id: string
  sheetId: string
  stageId: string
  type: MachineBreakDownType
  remark?: string | null
  startedAt: string
  endedAt?: string | null
  duration?: number
}

// Stage and Sheet types
export interface StageSheetResponse {
  stageId: string
  stageName: string
  type: StageType
  sequenceOrder: number
  breakdowns: BreakdownResponse[]
  readings?: ReadingResponse[]
}

export interface SheetResponse {
  id: string
  sessionId: string
  lineId: string
  lineName: string
  productId: string
  productName: string
  status: SheetStatus
  handoverId?: string | null
  handoverFromEmployeeCode?: string | null
  handoverFromEmployeeName?: string | null
  handoverFromSessionId?: string | null
  createdAt: string
  updatedAt: string
  stages: StageSheetResponse[]
}

// Parameters grouped by stage
export interface StageParametersResponse {
  stageId: string
  stageName: string
  type: StageType
  sequenceOrder: number
  parameters: ParameterResponse[]
}

export interface SheetParametersResponse {
  sheetId: string
  lineId: string
  lineName: string
  productId: string
  productName: string
  stages: StageParametersResponse[]
}

// ============================================================================
// API Response Types
// ============================================================================

// GET /ipqc/session/sheets
export interface GetSessionSheetsResponse {
  sessionId: string | null
  status: SessionStatus | null
  sheets: SheetResponse[]
}

// POST /ipqc/session/start
export interface StartSessionRequest {
  productId: string
}

export interface StartSessionResponse {
  sessionId: string
  sheets: SheetResponse[]
}

// POST /ipqc/session/products
export interface AddProductRequest {
  productId: string
}

export interface AddProductResponse {
  sheets: SheetResponse[]
}

// POST /ipqc/session/end
export interface EndSessionRequest {
  handoverEmployeeCode?: string
  handoverSheetIds?: string[]
}

export interface EndSessionResponse {
  success: boolean
  message: string
  handoverRequired?: boolean
  pendingSheetIds?: string[]
  employees?: SuggestedEmployee[]
  handoverId?: string
}

// GET /ipqc/session/check-handover
export interface CheckHandoverResponse {
  handoverRequired: boolean
  pendingSheetIds: string[]
  employees: SuggestedEmployee[]
}

// DELETE /ipqc/session
export interface DiscardSessionResponse {
  success: boolean
  message: string
  discardedSessionId?: string
  discardedSheetsCount?: number
}

export interface DeleteSheetResponse {
  success: boolean
  message: string
  deletedSheetId: string
}

// POST /ipqc/session/offline-sync
export interface OfflineSyncSheet {
  lineId: string
  productId: string
  name: string
  status: "DRAFT" | "SUBMITTED"
  readings: SheetReadingRequest[]
  breakdowns: OfflineIpqcBreakdown[]
}

export interface OfflineIpqcBreakdown extends BreakdownFormData {
  stageId: string
}

export interface OfflineSyncRequest {
  shiftId: string
  shiftSessionId: string
  processId: string
  clientSessionId: string
  startedAt: string
  selectedProducts: string[]
  sheets: OfflineSyncSheet[]
  handoverEmployeeCode?: string
}

export interface OfflineSyncResponse {
  success: boolean
  sessionId: string
  productsSaved?: number
  sheetsSaved?: number
  readingsSaved?: number
  breakdownsSaved?: number
}

// GET /ipqc/products
export interface ProductResponse {
  id: string
  name: string
  isActive: boolean
  createdAt: string
}

export interface GetProductsResponse {
  products: ProductResponse[]
}

// GET /ipqc/offline-bundle
export interface OfflineBundleContextResponse {
  plantId: string
  processId: string
  shiftId: string
  shiftName: string
  shiftSessionId: string
  shiftSessionStartedAt: string
  shiftStartDateTime: string
  shiftEndDateTime: string
  shiftStartTime: string
  shiftEndTime: string
}

export interface OfflineBundleWindowResponse {
  startTime: string
  endTime: string
  sequenceOrder?: number | null
}

export interface OfflineBundleParameterResponse extends ParameterResponse {
  productId: string
  stageId: string
  unitId?: string | null
  windows?: OfflineBundleWindowResponse[] | null
}

export interface OfflineBundleStageResponse {
  id: string
  name: string
  description?: string | null
  type: StageType
  sequenceOrder: number
  parameters: OfflineBundleParameterResponse[]
}

export interface OfflineBundleLineResponse {
  id: string
  name: string
  shiftName?: string | null
  shiftStartTime?: string | null
  shiftEndTime?: string | null
  stages: OfflineBundleStageResponse[]
}

export interface OfflineBundleProductResponse {
  id: string
  templateId?: string
  name: string
  productionLines: OfflineBundleLineResponse[]
}

export interface GetOfflineBundleResponse {
  bundleVersion: string
  bundleChecksum?: string
  generatedAt?: string
  context?: OfflineBundleContextResponse
  employees: SuggestedEmployee[]
  products: OfflineBundleProductResponse[]
}

// GET /ipqc/sheets/:id
export type GetSheetResponse = SheetResponse

// GET /ipqc/parameters
export interface GetParametersBySessionResponse {
  sheets: SheetParametersResponse[]
}

export interface GetParametersBySheetResponse {
  sheet: SheetParametersResponse
}

// POST /ipqc/readings
export interface ChildReadingRequest {
  value: string
  remark?: string
  recordedAt?: string
  gradeLabel?: string
  isFormula?: boolean
}

export interface ChildWindowRequest {
  value: string
  remark?: string
  recordedAt?: string
  gradeLabel?: string
  isFormula?: boolean
  overtime?: boolean
  early?: boolean
  sequenceOrder?: number
}

export interface WindowReadingRequest {
  startTime: string
  endTime: string
  value: string
  recordedAt?: string
  sequenceOrder?: number
  gradeLabel?: string
  remark?: string
  childWindows?: ChildWindowRequest[]
}

export interface SheetReadingRequest {
  parameterId: string
  value?: string
  gradeLabel?: string
  isFormula?: boolean
  remark?: string
  recordedAt?: string
  childReadings?: ChildReadingRequest[]
  windowReadings?: WindowReadingRequest[]
}

export interface SheetReadingsRequest {
  sheetId: string
  status: "DRAFT" | "SUBMITTED"
  readings: SheetReadingRequest[]
  breakdowns?: OfflineIpqcBreakdown[]
}

export interface SubmitSheetReadingsRequest {
  sheets: SheetReadingsRequest[]
}

export interface SubmitSheetReadingsResponse {
  success: boolean
  message: string
  sheetsUpdated: number
}

// POST /ipqc/breakdowns
export interface CreateBreakdownRequest {
  sheetId: string
  stageId: string
  type: MachineBreakDownType
  remark?: string
}

export type CreateBreakdownResponse = BreakdownResponse

// PATCH /ipqc/breakdowns/:id
export interface EndBreakdownRequest {
  endedAt?: string
}

export type EndBreakdownResponse = BreakdownResponse

// GET /ipqc/history
export interface HistorySheetResponse extends SheetResponse {
  completedAt?: string
  shiftName?: string
  shiftStartTime?: string
  shiftEndTime?: string
  shiftDate?: string
}

export interface GetHistoryResponse {
  found: boolean
  total: number
  sheets: HistorySheetResponse[]
}

export interface GetHistoryParams {
  limit?: number
  offset?: number
  productId?: string
  lineId?: string
  startDate?: string
  endDate?: string
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get the active IPQC session for the current employee
 */
export async function getSessionSheets(): Promise<GetSessionSheetsResponse> {
  return api.get<GetSessionSheetsResponse>("ipqc/session/sheets").json()
}

/**
 * Start a new IPQC session with a product
 */
export async function startSession(
  request: StartSessionRequest
): Promise<StartSessionResponse> {
  return api
    .post<StartSessionResponse>("ipqc/session/start", { json: request })
    .json()
}

/**
 * Add another product to an existing session
 */
export async function addProduct(
  request: AddProductRequest
): Promise<AddProductResponse> {
  return api
    .post<AddProductResponse>("ipqc/session/products", { json: request })
    .json()
}

/**
 * Check if handover is required before ending session
 */
export async function checkHandover(): Promise<CheckHandoverResponse> {
  return api.get<CheckHandoverResponse>("ipqc/session/check-handover").json()
}

/**
 * End the current session (may require handover)
 */
export async function endSession(
  request?: EndSessionRequest
): Promise<EndSessionResponse> {
  return api
    .post<EndSessionResponse>("ipqc/session/end", { json: request || {} })
    .json()
}

/**
 * Discard the current session
 */
export async function discardSession(): Promise<DiscardSessionResponse> {
  return api.delete<DiscardSessionResponse>("ipqc/session").json()
}

/**
 * Delete a single new/draft sheet from the current IPQC session
 */
export async function deleteSheet(sheetId: string): Promise<DeleteSheetResponse> {
  return api.delete<DeleteSheetResponse>(`ipqc/sheets/${sheetId}`).json()
}

/**
 * Get the full offline IPQC bundle for the current employee plant
 */
export async function getOfflineBundle(): Promise<GetOfflineBundleResponse> {
  return api.get<GetOfflineBundleResponse>("ipqc/offline-bundle").json()
}

/**
 * Get active products for the employee plant
 */
export async function getProducts(): Promise<GetProductsResponse> {
  return api.get<GetProductsResponse>("ipqc/products").json()
}

/**
 * Get full sheet details by ID
 */
export async function getSheet(sheetId: string): Promise<GetSheetResponse> {
  return api.get<GetSheetResponse>(`ipqc/sheets/${sheetId}`).json()
}

/**
 * Get parameters grouped by stages for a session
 */
export async function getParametersBySession(
  sessionId: string
): Promise<GetParametersBySessionResponse> {
  return api
    .get<GetParametersBySessionResponse>("ipqc/parameters", {
      searchParams: { sessionId },
    })
    .json()
}

/**
 * Get parameters grouped by stages for a sheet
 */
export async function getParametersBySheet(
  sheetId: string
): Promise<GetParametersBySheetResponse> {
  return api
    .get<GetParametersBySheetResponse>("ipqc/parameters", {
      searchParams: { sheetId },
    })
    .json()
}

/**
 * Submit readings grouped by sheet with status
 */
export async function submitSheetReadings(
  request: SubmitSheetReadingsRequest
): Promise<SubmitSheetReadingsResponse> {
  return api
    .post<SubmitSheetReadingsResponse>("ipqc/readings", { json: request })
    .json()
}

/**
 * Upload the complete offline IPQC session
 */
export async function syncOfflineSession(
  request: OfflineSyncRequest
): Promise<OfflineSyncResponse> {
  return api
    .post<OfflineSyncResponse>("ipqc/session/offline-sync", { json: request })
    .json()
}

/**
 * Create a breakdown for a stage
 */
export async function createBreakdown(
  request: CreateBreakdownRequest
): Promise<CreateBreakdownResponse> {
  return api
    .post<CreateBreakdownResponse>("ipqc/breakdowns", { json: request })
    .json()
}

/**
 * End a breakdown
 */
export async function endBreakdown(
  breakdownId: string,
  request?: EndBreakdownRequest
): Promise<EndBreakdownResponse> {
  return api
    .patch<EndBreakdownResponse>(`ipqc/breakdowns/${breakdownId}`, {
      json: request || {},
    })
    .json()
}

/**
 * Get completed sheets history
 */
export async function getHistory(
  params?: GetHistoryParams
): Promise<GetHistoryResponse> {
  const searchParams: Record<string, string | number> = {}
  if (params?.limit) searchParams.limit = params.limit
  if (params?.offset) searchParams.offset = params.offset
  if (params?.productId) searchParams.productId = params.productId
  if (params?.lineId) searchParams.lineId = params.lineId
  if (params?.startDate) searchParams.startDate = params.startDate
  if (params?.endDate) searchParams.endDate = params.endDate

  return api.get<GetHistoryResponse>("ipqc/history", { searchParams }).json()
}

// ============================================================================
// Adapter Layer - Maps new API responses to existing UI schema
// ============================================================================

/**
 * Map API ParameterType to UI ParamSchemaType
 */
function mapParameterType(type: ParameterType): ParamSchemaType {
  const typeMap: Record<ParameterType, ParamSchemaType> = {
    [ParameterType.VALUE_NUMBER]: ParamSchemaType.VALUE_NUMBER,
    [ParameterType.VALUE_TEXT]: ParamSchemaType.VALUE_TEXT,
    [ParameterType.CHOICE]: ParamSchemaType.CHOICE,
    [ParameterType.RADIO]: ParamSchemaType.RADIO,
    [ParameterType.DATE]: ParamSchemaType.DATE,
    [ParameterType.DROPDOWN]: ParamSchemaType.DROPDOWN,
  }
  return typeMap[type] || ParamSchemaType.VALUE_TEXT
}

/**
 * Map API ParameterMode to UI ParamSchemaMode
 */
function mapParameterMode(mode: ParameterMode): ParamSchemaMode {
  return mode === ParameterMode.TEST
    ? ParamSchemaMode.TEST
    : ParamSchemaMode.INFO
}

/**
 * Map API ParameterValidationType to UI ParamValidationType
 */
function mapValidationType(type: ParameterValidationType): ParamValidationType {
  const typeMap: Record<ParameterValidationType, ParamValidationType> = {
    [ParameterValidationType.LIMIT]: ParamValidationType.LIMIT,
    [ParameterValidationType.CHOICE]: ParamValidationType.CHOICE,
    [ParameterValidationType.FLAG]: ParamValidationType.FLAG,
    [ParameterValidationType.NONE]: ParamValidationType.NONE,
  }
  return typeMap[type] || ParamValidationType.NONE
}

/**
 * Map API ReadingStatus to UI FormParamStatus
 */
function mapReadingStatus(status: ReadingStatus): FormParamStatus {
  const statusMap: Record<ReadingStatus, FormParamStatus> = {
    [ReadingStatus.PENDING]: FormParamStatus.PENDING,
    [ReadingStatus.FILLED_COMPLETE]: FormParamStatus.FILLED_COMPLETE,
    [ReadingStatus.FILLED_PENDING_APPROVAL]:
      FormParamStatus.FILLED_PENDING_APPROVAL,
    [ReadingStatus.REJECTED]: FormParamStatus.REJECTED,
    [ReadingStatus.RESAMPLED]: FormParamStatus.RESAMPLED,
    [ReadingStatus.NOT_APPLICABLE]: FormParamStatus.NOT_APPLICABLE,
    [ReadingStatus.DRAFT]: FormParamStatus.DRAFT,
  }
  return statusMap[status] || FormParamStatus.PENDING
}

function isNotApplicableValue(value?: string | null): boolean {
  return value === FormParamStatus.NOT_APPLICABLE
}

/**
 * Map API SheetStatus to UI FormParamStatus
 */
function mapSheetStatus(status: SheetStatus): FormParamStatus {
  const statusMap: Record<SheetStatus, FormParamStatus> = {
    [SheetStatus.NEW]: FormParamStatus.NEW,
    [SheetStatus.DRAFT]: FormParamStatus.DRAFT,
    [SheetStatus.DELETED]: FormParamStatus.DRAFT,
    [SheetStatus.SUBMITTED]: FormParamStatus.SUBMITTED,
    [SheetStatus.COMPLETED]: FormParamStatus.COMPLETED,
    [SheetStatus.HANDOVER_NEW]: FormParamStatus.HANDOVER_NEW,
    [SheetStatus.HANDOVER_DRAFT]: FormParamStatus.HANDOVER_DRAFT,
    [SheetStatus.HANDOVER_SUBMITTED]: FormParamStatus.HANDOVER_SUBMITTED,
    [SheetStatus.HANDOVER_COMPLETED]: FormParamStatus.COMPLETED,
  }
  return statusMap[status] || FormParamStatus.PENDING
}

function isReadOnlyViewableSheetStatus(status: SheetStatus): boolean {
  return (
    status === SheetStatus.SUBMITTED ||
    status === SheetStatus.COMPLETED ||
    status === SheetStatus.HANDOVER_SUBMITTED ||
    status === SheetStatus.HANDOVER_COMPLETED
  )
}

/**
 * Map API grade label to UI ParamGradeLabel
 */
function mapGradeLabel(label?: string | null): ParamGradeLabel {
  if (!label) return ParamGradeLabel.None
  const normalized = label.toLowerCase()
  if (normalized === "green" || normalized === "ok" || normalized === "good") {
    return ParamGradeLabel.Green
  }
  if (
    normalized === "yellow" ||
    normalized === "warning" ||
    normalized === "high"
  ) {
    return ParamGradeLabel.Yellow
  }
  if (
    normalized === "orange" ||
    normalized === "error" ||
    normalized === "critical"
  ) {
    return ParamGradeLabel.Orange
  }
  return ParamGradeLabel.None
}

/**
 * Map API grade ranges to UI ParamGradeRange
 */
function mapGradeRanges(
  ranges?: ParameterGradeRangeResponse[] | null
): ParamGradeRange[] {
  if (!ranges) return []
  return ranges.map((range, idx) => ({
    id: `grade-${idx}`,
    min: range.minValue,
    max: range.maxValue,
    gradeLabel: mapGradeLabel(range.label),
  }))
}

/**
 * Map API choices to UI ParamChoice
 */
function mapChoices(choices?: ParameterChoiceResponse[] | null): ParamChoice[] {
  if (!choices) return []
  return choices.map((choice, idx) => ({
    id: `choice-${idx}`,
    value: choice.value,
    label: choice.label,
    isCorrect: choice.isCorrect,
  }))
}

/**
 * Map API parameter limits to UI ParamSchemaLimit
 */
function mapLimits(param: ParameterResponse): ParamSchemaLimit | undefined {
  if (
    param.validationType !== ParameterValidationType.LIMIT ||
    param.minLimit == null ||
    param.maxLimit == null
  ) {
    return undefined
  }
  return {
    min: param.minLimit,
    max: param.maxLimit,
    gradeRanges: mapGradeRanges(param.gradeRanges),
  }
}

/**
 * Generate time windows for a parameter based on shift times and interval
 * Only creates complete windows that fit within the shift duration
 * Handles overnight shifts (end time before start time)
 */
function generateTimeWindows(
  intervalMinutes: number,
  shiftStartTime?: string,
  shiftEndTime?: string
): ParamWindow[] {
  if (!shiftStartTime || !shiftEndTime) return []

  const windows: ParamWindow[] = []
  const intervalMs = intervalMinutes * 60 * 1000

  // Parse shift times
  const shiftStart = new Date(shiftStartTime)
  let shiftEnd = new Date(shiftEndTime)

  // Handle overnight shift (end time is before start time)
  if (shiftEnd <= shiftStart) {
    shiftEnd.setDate(shiftEnd.getDate() + 1)
  }

  let currentWindowStart = new Date(shiftStart)
  let windowIndex = 0

  while (currentWindowStart < shiftEnd) {
    const windowEnd = new Date(currentWindowStart.getTime() + intervalMs)

    // Don't create a window that extends past shift end
    if (windowEnd > shiftEnd) {
      break
    }

    windows.push({
      id: `window-${windowIndex}`,
      startTime: currentWindowStart.toISOString(),
      endTime: windowEnd.toISOString(),
    })

    currentWindowStart = windowEnd
    windowIndex++
  }

  return windows
}

/**
 * Extract time portion from ISO string for comparison (HH:MM format)
 */
function extractTimeKey(isoString: string): string {
  const date = new Date(isoString)
  const hours = date.getUTCHours().toString().padStart(2, "0")
  const minutes = date.getUTCMinutes().toString().padStart(2, "0")
  return `${hours}:${minutes}`
}

function normalizeResponseValue(value?: string | null): string | undefined {
  if (value == null || value === "") {
    return undefined
  }

  return value
}

function mapChildReadingToResample(
  childReading?: ReadingResponse
): ParamSchema["resample"] | undefined {
  const value = normalizeResponseValue(childReading?.value)

  if (!value) {
    return undefined
  }

  return {
    value,
    remark: childReading?.remark || undefined,
    recordedAt: childReading?.recordedAt || undefined,
    gradeLabel: childReading?.gradeLabel
      ? mapGradeLabel(childReading.gradeLabel)
      : undefined,
  }
}

function mapChildWindowToResample(
  childWindow: WindowReadingResponse | undefined,
  parentWindow: ParamWindow
): ParamWindow["resample"] | undefined {
  const value = normalizeResponseValue(childWindow?.value)

  if (!value) {
    return undefined
  }

  return {
    startTime: parentWindow.startTime,
    endTime: parentWindow.endTime,
    value,
    remark: childWindow?.remark || undefined,
    recordedAt: childWindow?.recordedAt || undefined,
    gradeLabel: childWindow?.gradeLabel
      ? mapGradeLabel(childWindow.gradeLabel)
      : undefined,
    sequenceOrder: childWindow?.sequenceOrder ?? undefined,
    overtime: childWindow?.overtime ?? undefined,
    early: childWindow?.early ?? undefined,
  }
}

/**
 * Map API ParameterResponse to UI ParamSchema
 */
function mapParameterToSchema(
  param: ParameterResponse,
  reading?: ReadingResponse,
  shiftStartTime?: string,
  shiftEndTime?: string
): ParamSchema {
  const mappedChoices = mapChoices(param.choices)
  const mappedType = mapParameterType(param.type)

  const baseSchema: ParamSchema = {
    id: reading?.id,
    parameterId: param.id,
    name: param.name,
    subtext: param.description || undefined,
    mode: mapParameterMode(param.mode),
    type: mappedType,
    validationType: mapValidationType(param.validationType),
    unit: param.unit || "",
    limit: mapLimits(param),
    choices: mappedChoices,
  }

  // Add options for DROPDOWN type
  if (mappedType === ParamSchemaType.DROPDOWN && param.choices) {
    baseSchema.options = param.choices.map((choice, idx) => ({
      id: `option-${idx}`,
      label: choice.label,
      value: choice.value,
    }))
  }

  // Add formula if present
  if (param.formulaText && param.formulaInputs) {
    baseSchema.formula = {
      id: `formula-${param.id}`,
      formulaText: param.formulaText,
      inputs: param.formulaInputs.map((input, idx) => ({
        id: `input-${idx}`,
        variable: input.variable,
        name: input.name,
        type: ParamSchemaType.VALUE_NUMBER,
        unit: input.unit || "",
      })),
      factors: param.formulaFactors || undefined,
    }
  }

  // Handle windowed parameters
  if (param.intervalMinutes && param.intervalMinutes > 0) {
    // Always generate all time windows based on interval
    const generatedWindows = generateTimeWindows(
      param.intervalMinutes,
      shiftStartTime,
      shiftEndTime
    )
    const apiWindowReadings = reading?.windowReadings || []

    // If shift bounds are unavailable, preserve API windows to avoid collapsing to single reading UI.
    if (!generatedWindows.length && apiWindowReadings.length) {
      baseSchema.window = [...apiWindowReadings]
        .sort((a, b) => {
          const aOrder = a.sequenceOrder ?? Number.MAX_SAFE_INTEGER
          const bOrder = b.sequenceOrder ?? Number.MAX_SAFE_INTEGER

          if (aOrder !== bOrder) {
            return aOrder - bOrder
          }

          return (
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
          )
        })
        .map((apiWindow, index) => {
          const normalizedStatus = isNotApplicableValue(apiWindow.value)
            ? FormParamStatus.NOT_APPLICABLE
            : mapReadingStatus(apiWindow.status)

          const mappedWindow: ParamWindow = {
            id: apiWindow.id || `api-window-${index}`,
            startTime: apiWindow.startTime,
            endTime: apiWindow.endTime,
            value: isNotApplicableValue(apiWindow.value)
              ? undefined
              : apiWindow.value || undefined,
            status: normalizedStatus,
            remark: apiWindow.remark || undefined,
            recordedAt: apiWindow.recordedAt || undefined,
            sequenceOrder: apiWindow.sequenceOrder ?? undefined,
            overtime: apiWindow.overtime ?? undefined,
            early: apiWindow.early ?? undefined,
          }

          mappedWindow.resample = mapChildWindowToResample(
            apiWindow.childWindows?.[0],
            mappedWindow
          )

          return mappedWindow
        })
    } else {
      // Create a map of existing window readings by time range for quick lookup
      // Use only time portion (HH:MM) for matching since API uses 1970-01-01 as placeholder date
      const existingWindowsMap = new Map<string, WindowReadingResponse>()
      reading?.windowReadings?.forEach((wr) => {
        const startTimeKey = extractTimeKey(wr.startTime)
        const endTimeKey = extractTimeKey(wr.endTime)
        const key = `${startTimeKey}-${endTimeKey}`
        existingWindowsMap.set(key, wr)
      })

      // Merge generated windows with existing readings
      baseSchema.window = generatedWindows.map((genWindow) => {
        const startTimeKey = extractTimeKey(genWindow.startTime)
        const endTimeKey = extractTimeKey(genWindow.endTime)
        const key = `${startTimeKey}-${endTimeKey}`
        const existingReading = existingWindowsMap.get(key)

        if (existingReading) {
          const normalizedStatus = isNotApplicableValue(existingReading.value)
            ? FormParamStatus.NOT_APPLICABLE
            : mapReadingStatus(existingReading.status)

          // Use values from existing reading
          const mappedWindow: ParamWindow = {
            id: existingReading.id,
            startTime: genWindow.startTime,
            endTime: genWindow.endTime,
            value: isNotApplicableValue(existingReading.value)
              ? undefined
              : existingReading.value || undefined,
            status: normalizedStatus,
            remark: existingReading.remark || undefined,
            recordedAt: existingReading.recordedAt || undefined,
          }

          mappedWindow.resample = mapChildWindowToResample(
            existingReading.childWindows?.[0],
            mappedWindow
          )

          return mappedWindow
        }

        // Return generated window without values
        return genWindow
      })
    }
  } else if (reading) {
    const normalizedStatus = isNotApplicableValue(reading.value)
      ? FormParamStatus.NOT_APPLICABLE
      : mapReadingStatus(reading.status)

    // Single value reading
    baseSchema.value = isNotApplicableValue(reading.value)
      ? undefined
      : reading.value || undefined
    baseSchema.status = normalizedStatus
    baseSchema.remark = reading.remark || undefined
    baseSchema.recordedAt = reading.recordedAt || undefined
    baseSchema.gradeLabel = mapGradeLabel(reading.gradeLabel)
    baseSchema.resample = mapChildReadingToResample(reading.childReadings?.[0])
  }

  return baseSchema
}

/**
 * Map API StageSheetResponse to UI InternalProcessSchema
 */
function mapStageToInternalProcess(
  stage: StageSheetResponse,
  params: ParameterResponse[],
  shiftStartTime?: string,
  shiftEndTime?: string
): InternalProcessSchema {
  // Create a map of readings by parameterId for quick lookup
  const readingsMap = new Map<string, ReadingResponse>()
  stage.readings?.forEach((reading) => {
    readingsMap.set(reading.parameterId, reading)
  })

  return {
    id: stage.stageId,
    name: stage.stageName,
    parameters: params.map((param) =>
      mapParameterToSchema(
        param,
        readingsMap.get(param.id),
        shiftStartTime,
        shiftEndTime
      )
    ),
  }
}

/**
 * Map API breakdowns to UI BreakdownFormData
 */
function mapBreakdowns(breakdowns: BreakdownResponse[]): BreakdownFormData[] {
  return breakdowns.map((bd) => ({
    id: bd.id,
    type: bd.type,
    remark: bd.remark || "",
    startedAt: bd.startedAt,
    endedAt: bd.endedAt || "",
  }))
}

function mapOfflineBundleParameterToSchema(
  param: OfflineBundleParameterResponse
): ParamSchema {
  const mappedChoices = mapChoices(param.choices)
  const mappedType = mapParameterType(param.type)

  const schema: ParamSchema = {
    parameterId: param.id,
    productId: param.productId,
    name: param.name,
    subtext: param.description || undefined,
    mode: mapParameterMode(param.mode),
    type: mappedType,
    validationType: mapValidationType(param.validationType),
    unit: param.unit || "",
    limit: mapLimits(param),
    choices: mappedChoices,
  }

  if (mappedType === ParamSchemaType.DROPDOWN && param.choices) {
    schema.options = param.choices.map((choice, idx) => ({
      id: `option-${param.id}-${idx}`,
      label: choice.label,
      value: choice.value,
    }))
  }

  if (param.formulaText && param.formulaInputs) {
    schema.formula = {
      id: `formula-${param.id}`,
      formulaText: param.formulaText,
      inputs: param.formulaInputs.map((input, idx) => ({
        id: `input-${param.id}-${idx}`,
        variable: input.variable,
        name: input.name,
        type: ParamSchemaType.VALUE_NUMBER,
        unit: input.unit || "",
      })),
      factors: param.formulaFactors || undefined,
    }
  }

  if (param.windows?.length) {
    schema.window = param.windows.map((window, index) => ({
      id: `${param.id}-window-${index + 1}`,
      startTime: window.startTime,
      endTime: window.endTime,
      sequenceOrder: window.sequenceOrder ?? index + 1,
      status: FormParamStatus.PENDING,
    }))
  }

  return schema
}

function mapOfflineBundleProductToSchema(
  product: OfflineBundleProductResponse
): ProductSchema {
  return {
    id: product.id,
    name: product.name,
    productionLines: product.productionLines.map((line) => ({
      id: line.id,
      name: line.name,
      status: FormParamStatus.PENDING,
      shiftName: line.shiftName || undefined,
      shiftStartTime: line.shiftStartTime || undefined,
      shiftEndTime: line.shiftEndTime || undefined,
      machines: [...line.stages]
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map((stage) => ({
          id: stage.id,
          name: stage.name,
          breakdown: [],
          internalProcesses: [
            {
              id: stage.id,
              name: "DEFAULT",
              parameters: [...stage.parameters]
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map(mapOfflineBundleParameterToSchema),
            },
          ],
        })),
    })),
  }
}

/**
 * Map a stage to UI MachineSchema
 * In the new schema, each stage becomes a "machine" with a single internal process
 */
function mapStageToMachine(
  stage: StageSheetResponse,
  stageParams: StageParametersResponse | undefined,
  shiftStartTime?: string,
  shiftEndTime?: string
): MachineSchema {
  const params = stageParams?.parameters || []

  return {
    id: stage.stageId,
    name: stage.stageName,
    breakdown: mapBreakdowns(stage.breakdowns),
    internalProcesses: [
      mapStageToInternalProcess(stage, params, shiftStartTime, shiftEndTime),
    ],
  }
}

/**
 * Map API SheetResponse to UI ProductionLineSchema
 */
function mapSheetToProductionLine(
  sheet: SheetResponse,
  sheetParams: SheetParametersResponse | undefined,
  shiftStartTime?: string,
  shiftEndTime?: string
): ProductionLineSchema {
  // Create a map of stage parameters for quick lookup
  const stageParamsMap = new Map<string, StageParametersResponse>()
  sheetParams?.stages.forEach((sp) => {
    stageParamsMap.set(sp.stageId, sp)
  })

  return {
    id: sheet.lineId,
    serverSheetId: sheet.id,
    name: sheet.lineName,
    status: mapSheetStatus(sheet.status),
    filledAt: sheet.updatedAt,
    shiftStartTime,
    shiftEndTime,
    machines: sheet.stages
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map((stage) =>
        mapStageToMachine(
          stage,
          stageParamsMap.get(stage.stageId),
          shiftStartTime,
          shiftEndTime
        )
      ),
  }
}

function normalizePersistedProductionLineIdentity(
  productId: string,
  line: ProductionLineSchema,
  bundle: OfflineIpqcBundle | null | undefined
): ProductionLineSchema {
  if (line.serverSheetId) {
    return line
  }

  const bundleProduct = bundle?.products.find((product) => product.id === productId)
  const bundleLine = bundleProduct?.productionLines.find(
    (candidate) => candidate.name === line.name
  )

  if (!bundleLine) {
    return line
  }

  if (bundleLine.id === line.id) {
    return line
  }

  return {
    ...line,
    id: bundleLine.id,
    serverSheetId: line.id,
  }
}

function normalizePersistedProductLineIdentity(
  product: ProductSchema,
  bundle: OfflineIpqcBundle | null | undefined
): ProductSchema {
  return {
    ...product,
    productionLines: product.productionLines.map((line) =>
      normalizePersistedProductionLineIdentity(product.id, line, bundle)
    ),
  }
}

/**
 * Group sheets by productId and map to ProductSchema
 */
function groupSheetsByProduct(
  sheets: SheetResponse[],
  sheetsParams: SheetParametersResponse[],
  shiftStartTime?: string,
  shiftEndTime?: string
): ProductSchema[] {
  // Sort sheets by createdAt so products and lines appear in creation order
  const sortedSheets = [...sheets].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  // Group sheets by productId
  const productMap = new Map<
    string,
    {
      id: string
      name: string
      sheets: SheetResponse[]
      params: SheetParametersResponse[]
    }
  >()

  sortedSheets.forEach((sheet) => {
    const existing = productMap.get(sheet.productId)
    const sheetParam = sheetsParams.find((sp) => sp.sheetId === sheet.id)

    if (existing) {
      existing.sheets.push(sheet)
      if (sheetParam) existing.params.push(sheetParam)
    } else {
      productMap.set(sheet.productId, {
        id: sheet.productId,
        name: sheet.productName,
        sheets: [sheet],
        params: sheetParam ? [sheetParam] : [],
      })
    }
  })

  // Convert to ProductSchema array
  return dedupeProductSchemas(
    Array.from(productMap.values()).map((product) => ({
      id: product.id,
      name: product.name,
      productionLines: product.sheets.map((sheet) => {
        const sheetParam = product.params.find((p) => p.sheetId === sheet.id)
        return mapSheetToProductionLine(
          sheet,
          sheetParam,
          shiftStartTime,
          shiftEndTime
        )
      }),
    }))
  )
}

// ============================================================================
// High-Level API Functions with Store Integration
// ============================================================================

export interface IPQCSessionData {
  sessionId: string | null
  status: SessionStatus | null
  products: ProductSchema[]
  handovers: HandoverFormSchema[]
}

const AGGREGATED_IPQC_HANDOVER_SESSION_ID = "__ipqc_handover__"

function aggregateIpqcHandovers(params: {
  handovers: HandoverFormSchema[]
  handoverForms?: FormData[]
  lineStatus?: Record<string, FormParamStatus | undefined>
  preserveActiveHandover?: boolean
}) {
  if (!params.handovers.length) {
    return {
      handovers: [] as HandoverFormSchema[],
      handoverForms: [] as FormData[],
      lineStatus: params.lineStatus || {},
      currHandoverIdx: -1,
      currHandoverSessionId: "",
    }
  }

  const aggregatedSchema: HandoverFormSchema = {
    handoverFromEmployeeCode: "",
    handoverFromEmployeeSessionId: AGGREGATED_IPQC_HANDOVER_SESSION_ID,
    products: params.handovers.flatMap((handover) => handover.products),
  }

  const aggregatedForm: FormData = {
    handoverFromEmployeeCode: "",
    handoverFromEmployeeSessionId: AGGREGATED_IPQC_HANDOVER_SESSION_ID,
    products: (params.handoverForms || []).flatMap((handover) => handover.products),
  }

  const handoverSessionIds = new Set(
    params.handovers.map((handover) => handover.handoverFromEmployeeSessionId)
  )
  const nextLineStatus = Object.entries(params.lineStatus || {}).reduce(
    (acc, [key, value]) => {
      const parts = toLineStatusKeyParts(key)
      const normalizedKey = handoverSessionIds.has(parts.sessionId)
        ? toLineStatusKey({
            productId: parts.productId,
            lineId: parts.lineId,
            sessionId: AGGREGATED_IPQC_HANDOVER_SESSION_ID,
          })
        : key

      acc[normalizedKey] = value
      return acc
    },
    {} as Record<string, FormParamStatus | undefined>
  )

  return {
    handovers: [aggregatedSchema],
    handoverForms: [aggregatedForm],
    lineStatus: nextLineStatus,
    currHandoverIdx: params.preserveActiveHandover ? 0 : -1,
    currHandoverSessionId: params.preserveActiveHandover
      ? AGGREGATED_IPQC_HANDOVER_SESSION_ID
      : "",
  }
}

/**
 * Fetch session and parameters, returning adapted schema
 */
export async function fetchSessionWithSchema(
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<IPQCSessionData> {
  const sessionRes = await getSessionSheets()
  const availableSheets = sessionRes.sheets.filter(
    (sheet) => sheet.status !== SheetStatus.DELETED
  )

  if (!sessionRes.sessionId || !availableSheets.length) {
    return {
      sessionId: null,
      status: null,
      products: [],
      handovers: [],
    }
  }

  // Fetch parameters for all sheets
  const paramsRes = await getParametersBySession(sessionRes.sessionId)

  // Separate own sheets from handover sheets
  const ownSheets = availableSheets.filter((s) => !s.handoverId)
  const handoverSheets = availableSheets.filter((s) => s.handoverId)
  const readOnlySheets = availableSheets.filter((sheet) =>
    isReadOnlyViewableSheetStatus(sheet.status)
  )

  // For ended sessions, only show submitted/completed sheets in read-only view.
  const sheetsToProcess =
    sessionRes.status === SessionStatus.ENDED ? readOnlySheets : ownSheets

  // Get parameters for own sheets
  const sheetsParams = paramsRes.sheets.filter((p) =>
    sheetsToProcess.some((s) => s.id === p.sheetId)
  )

  // Map own sheets to products
  const products = groupSheetsByProduct(
    sheetsToProcess,
    sheetsParams,
    shiftStartTime,
    shiftEndTime
  )

  // Process handover sheets (skip for ended sessions — already included above)
  const handovers: HandoverFormSchema[] = []
  if (sessionRes.status !== SessionStatus.ENDED && handoverSheets.length) {
    // Group handover sheets by handoverId first, then aggregate all groups into one UI handover bucket.
    const handoverGroups = new Map<string, SheetResponse[]>()
    for (const sheet of handoverSheets) {
      const key = sheet.handoverId!
      const group = handoverGroups.get(key)
      if (group) {
        group.push(sheet)
      } else {
        handoverGroups.set(key, [sheet])
      }
    }

    for (const [, sheets] of handoverGroups) {
      const firstSheet = sheets[0]
      const handoverParams = paramsRes.sheets.filter((p) =>
        sheets.some((s) => s.id === p.sheetId)
      )

      const handoverProducts = groupSheetsByProduct(
        sheets,
        handoverParams,
        undefined,
        undefined
      ).map(
        (product): HandoverProductSchema => ({
          ...product,
          plantName: firstSheet.lineName,
          shiftName: "",
          employeeName: firstSheet.handoverFromEmployeeName || "",
          createdAt: firstSheet.createdAt,
        })
      )

      handovers.push({
        handoverFromEmployeeCode:
          firstSheet.handoverFromEmployeeCode || "Handover",
        handoverFromEmployeeSessionId:
          firstSheet.handoverFromSessionId || firstSheet.handoverId!,
        products: handoverProducts,
      })
    }
  }

  const aggregatedHandovers = aggregateIpqcHandovers({
    handovers,
  }).handovers

  return {
    sessionId: sessionRes.sessionId,
    status: sessionRes.status,
    products,
    handovers: aggregatedHandovers,
  }
}

/**
 * Start a new session and return adapted schema
 */
export async function startSessionWithSchema(
  productId: string,
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<IPQCSessionData> {
  const startRes = await startSession({ productId })

  // Fetch parameters for the new sheets
  const paramsRes = await getParametersBySession(startRes.sessionId)

  const products = groupSheetsByProduct(
    startRes.sheets,
    paramsRes.sheets,
    shiftStartTime,
    shiftEndTime
  )

  return {
    sessionId: startRes.sessionId,
    status: SessionStatus.STARTED,
    products,
    handovers: [],
  }
}

/**
 * Add a product to existing session and return updated schema
 */
export async function addProductWithSchema(
  productId: string,
  _existingSessionId: string,
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<ProductSchema[]> {
  void _existingSessionId // Reserved for future use
  const addRes = await addProduct({ productId })

  // Fetch parameters for the new sheets
  const newSheetIds = addRes.sheets.map((s) => s.id)
  const allParams: SheetParametersResponse[] = []

  for (const sheetId of newSheetIds) {
    const paramRes = await getParametersBySheet(sheetId)
    allParams.push(paramRes.sheet)
  }

  return groupSheetsByProduct(
    addRes.sheets,
    allParams,
    shiftStartTime,
    shiftEndTime
  )
}

/**
 * Hydrate IPQC store from session data
 */
export function hydrateStoreFromSession(
  data: IPQCSessionData,
  processId?: string,
  shiftId?: string
): void {
  const store = useStore.getState()
  const ipqcStore = store.ipqc

  // Clear existing data
  ipqcStore.clearFormData()

  // Calculate line statuses first (needed for schemaToFormData)
  const lineStatuses: Record<string, FormParamStatus | undefined> = {}
  data.products.forEach((prod) => {
    prod.productionLines.forEach((line) => {
      lineStatuses[toLineStatusKey({ productId: prod.id, lineId: line.id })] =
        line.status || FormParamStatus.PENDING
    })
  })

  // Also set lineStatuses for handover products
  data.handovers.forEach((handover) => {
    handover.products.forEach((prod) => {
      prod.productionLines.forEach((line) => {
        lineStatuses[
          toLineStatusKey({
            productId: prod.id,
            lineId: line.id,
            sessionId: handover.handoverFromEmployeeSessionId,
          })
        ] = line.status || FormParamStatus.PENDING
      })
    })
  })

  ipqcStore.setLineStatuses(lineStatuses)

  // Set schemas and populate form data
  if (data.products.length) {
    ipqcStore.setSelfSchema({ products: data.products })

    // Convert schema to form data to populate values from existing readings
    const formData = schemaToFormData(
      { products: data.products },
      lineStatuses,
      "self"
    )
    ipqcStore.setSelfFormData(formData)
  }

  // Set handover schemas
  if (data.handovers.length) {
    ipqcStore.setHandoverSchemas(data.handovers)

    data.handovers.forEach((handover) => {
      const handoverFormData = schemaToFormData(
        { products: handover.products },
        lineStatuses,
        handover.handoverFromEmployeeSessionId
      )

      ipqcStore.setHandoverFormData(
        {
          handoverFromEmployeeCode: handover.handoverFromEmployeeCode,
          handoverFromEmployeeSessionId: handover.handoverFromEmployeeSessionId,
          ...handoverFormData,
        },
        handover.handoverFromEmployeeSessionId
      )
    })
  }

  // Update shift details
  if (processId || shiftId) {
    ipqcStore.setShiftDetails({
      processId,
      shiftId,
    })
  }
}

function buildHydratedOfflineSessionMeta(params: {
  serverSessionId: string
}): OfflineIpqcSession["meta"] {
  const store = useStore.getState()
  const activeSession = store.ipqc.offline.activeSession
  const timestamp = new Date().toISOString()

  return {
    clientSessionId: activeSession?.clientSessionId || createUuid(),
    serverSessionId: params.serverSessionId,
    shiftId: store.session.shift.id,
    shiftSessionId: store.session.session.id,
    processId: store.ipqc.shift.processId,
    startedAt: activeSession?.startedAt || timestamp,
    employeeCode: store.user.employee.code,
    plantId: store.user.plant.id,
    isDirty: false,
    hasDeferredSync: activeSession?.hasDeferredSync || false,
    syncedAt: timestamp,
    pendingFinalizeAt: activeSession?.pendingFinalizeAt,
    handoverEmployeeCode: activeSession?.handoverEmployeeCode,
  }
}

function normalizeOfflineIpqcSession(
  session: OfflineIpqcSession
): OfflineIpqcSession {
  const bundle = useStore.getState().ipqc.offline.bundle
  const normalizedSchema: FormSchema = {
    ...session.schema,
    products: session.schema.products.map((product) =>
      normalizePersistedProductLineIdentity(product, bundle)
    ),
  }
  const dedupedSelfSession = dedupeProductsWithForms({
    products: normalizedSchema.products,
    formProducts: session.form.products,
  })
  const handoverSchemas = (session.handoverSchemas || []).map((handover) => ({
    ...handover,
    products: handover.products.map((product) => ({
      ...product,
      ...normalizePersistedProductLineIdentity(product, bundle),
    })),
  }))
  const handoverForms = session.handoverForms || []
  const normalizedSession: OfflineIpqcSession = {
    ...session,
    meta: {
      ...session.meta,
      hasDeferredSync:
        session.meta.hasDeferredSync ||
        hasDeferredSyncLines({ products: dedupedSelfSession.products }),
    },
    schema: {
      ...normalizedSchema,
      products: dedupedSelfSession.products,
    },
    form: {
      ...session.form,
      products: dedupedSelfSession.formProducts,
    },
    handoverSchemas,
  }

  if (handoverSchemas.length <= 1) {
    return {
      ...normalizedSession,
      handoverSchemas,
      handoverForms,
    }
  }

  const aggregated = aggregateIpqcHandovers({
    handovers: handoverSchemas,
    handoverForms,
    lineStatus: normalizedSession.lineStatus,
    preserveActiveHandover: !!normalizedSession.currHandoverSessionId,
  })

  return {
    ...normalizedSession,
    handoverSchemas: aggregated.handovers,
    handoverForms: aggregated.handoverForms,
    lineStatus: aggregated.lineStatus,
    currHandoverIdx: aggregated.currHandoverIdx,
    currHandoverSessionId: aggregated.currHandoverSessionId,
  }
}

async function persistHydratedOnlineSession(params: {
  serverSessionId: string
}): Promise<OfflineIpqcSession> {
  const store = useStore.getState()
  const meta = buildHydratedOfflineSessionMeta(params)

  store.ipqc.startOfflineSession(meta)

  const snapshot = createOfflineSessionSnapshot()
  await saveOfflineIpqcSession(snapshot)

  return snapshot
}

export async function hydrateOnlineIpqcSession(
  shiftStartTime?: string,
  shiftEndTime?: string,
  options?: { persistOfflineSession?: boolean }
): Promise<IPQCSessionData | null> {
  const store = useStore.getState()
  const sessionData = await fetchSessionWithSchema(shiftStartTime, shiftEndTime)
  const shouldPersistOfflineSession = options?.persistOfflineSession ?? true

  if (!sessionData.sessionId) {
    return null
  }

  hydrateStoreFromSession(
    sessionData,
    store.ipqc.shift.processId,
    store.session.shift.id
  )
  if (shouldPersistOfflineSession) {
    await persistHydratedOnlineSession({
      serverSessionId: sessionData.sessionId,
    })
  }

  return sessionData
}

export async function startOrResumeOnlineIpqcSession(
  productIds: string[],
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<IPQCSessionData> {
  if (!productIds.length) {
    throw new Error("Select at least one IPQC product")
  }

  const existingSession = await hydrateOnlineIpqcSession(
    shiftStartTime,
    shiftEndTime
  )

  if (existingSession) {
    return existingSession
  }

  const emptySession = await getSessionSheets()
  const emptySessionActiveSheets = emptySession.sheets.filter(
    (sheet) => sheet.status !== SheetStatus.DELETED
  )
  if (emptySession.sessionId && !emptySessionActiveSheets.length) {
    for (const productId of productIds) {
      await addProduct({ productId })
    }

    const resumedSession = await hydrateOnlineIpqcSession(
      shiftStartTime,
      shiftEndTime
    )

    if (!resumedSession) {
      throw new Error("Failed to load the resumed IPQC session")
    }

    return resumedSession
  }

  const [firstProductId, ...remainingProductIds] = productIds
  await startSession({ productId: firstProductId })

  for (const productId of remainingProductIds) {
    await addProduct({ productId })
  }

  const startedSession = await hydrateOnlineIpqcSession(
    shiftStartTime,
    shiftEndTime
  )

  if (!startedSession) {
    throw new Error("Failed to load the started IPQC session")
  }

  return startedSession
}

// ============================================================================
// Error Handling & Sync Helpers
// ============================================================================

/**
 * Check if an error is sheet-related and requires state reset
 */
export function isSheetSyncError(errorBody: unknown): boolean {
  if (!errorBody || typeof errorBody !== "object") return false
  const body = errorBody as Record<string, unknown>
  const code = String(body.error_code || body.code || "")
  const message = String(
    body.error_message || body.message || body.user_message || ""
  )
  return (
    code.includes("SHEET") ||
    code.includes("DUPLICATE") ||
    code.includes("SUBMITTED") ||
    code.includes("IPQC_") ||
    message.toLowerCase().includes("sheet") ||
    message.toLowerCase().includes("already submitted") ||
    message.toLowerCase().includes("not found") ||
    message.toLowerCase().includes("cannot change") ||
    message.toLowerCase().includes("no sheets") ||
    message.toLowerCase().includes("no active session")
  )
}

/**
 * Resets local IPQC state and rehydrates from backend.
 * Call this when backend returns sheet-related errors.
 */
export async function resetAndRehydrateIPQC(
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<void> {
  const store = useStore.getState()

  // 1. Clear all local IPQC data
  store.ipqc.clearFormData()

  // 2. Fetch fresh data from server
  try {
    const freshData = await hydrateOnlineIpqcSession(
      shiftStartTime,
      shiftEndTime
    )
    if (!freshData) {
      throw new Error("No active IPQC session found during rehydration")
    }

    console.info("[resetAndRehydrateIPQC] Successfully rehydrated from server")
  } catch (rehydrateErr) {
    console.error("[resetAndRehydrateIPQC] Failed to rehydrate:", rehydrateErr)
    // Even if rehydration fails, local state is cleared
    throw rehydrateErr
  }
}

// ============================================================================
// Sync Hook - Auto-save readings periodically
// ============================================================================

import { useEffect, useRef } from "react"
import { isFilled, schemaToFormData } from "@/ipqc/lib/utils"
import {
  OfflineIpqcBundle,
  OfflineIpqcEmployee,
  OfflineIpqcSession,
  loadOfflineIpqcSession,
  saveOfflineIpqcBundle,
  saveOfflineIpqcSession,
} from "@/ipqc/lib/offline-store"
import {
  FormData,
  SingleValueParamData,
  WindowParamFormData,
} from "@/lib/form/types"
import { AutoSaveStatus } from "@/rmpm/lib/auto-save-indicator"

export interface UseSyncSheetProps {
  form: {
    getValues: () => FormData
    addChangeHandler: (
      name: string,
      handler: (...args: any[]) => void | Promise<void>,
      options?: { deep?: boolean }
    ) => number
    removeChangeHandler: (name: string, hash: number) => void
  }
  schema?: FormSchema | HandoverFormSchema
  enabled?: boolean
  onStatusChange?: (status: AutoSaveStatus) => void
}

/**
 * Hook to auto-sync form data to the server after field changes
 */
export function useSyncSheet({
  form,
  schema,
  enabled = true,
  onStatusChange,
}: UseSyncSheetProps) {
  const lineStatus = useStore.use.ipqc().lineStatus
  const sessionId = useStore.use.ipqc().currHandoverSessionId || "self"
  const isReadOnly = useStore.use.ipqc().isReadOnly
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const syncInFlightRef = useRef(false)
  const syncQueuedRef = useRef(false)

  useEffect(() => {
    function isRecoverableNetworkSubmitError(error: unknown): boolean {
      return (
        isNetworkUnavailableError(error) ||
        (error instanceof Error && error.name === "AbortError")
      )
    }

    async function syncDraftToStorage() {
      try {
        if (!enabled || isReadOnly) {
          onStatusChange?.("idle")
          return
        }

        if (schema) {
          onStatusChange?.("saving")
          const store = useStore.getState()
          const activeSession = store.ipqc.offline.activeSession
          const currentForm = form.getValues()

          if (activeSession) {
            store.ipqc.updateOfflineSessionMeta({ isDirty: true })
            const session = createOfflineSessionSnapshot({
              schema,
              form: currentForm,
              lineStatus,
              sessionId,
            })
            await saveOfflineIpqcSession(session)

            if (
              activeSession.serverSessionId &&
              (typeof navigator === "undefined" || navigator.onLine !== false)
            ) {
              try {
                const syncResult = await submitAllSheetsNew({
                  schema,
                  form: currentForm,
                  lineStatus,
                  sessionId,
                })
                const shouldStayDirty =
                  !!activeSession.hasDeferredSync ||
                  syncResult.skippedLocalOnlySheetCount > 0 ||
                  hasDeferredSyncLines(schema)

                store.ipqc.updateOfflineSessionMeta({
                  isDirty: shouldStayDirty,
                  syncedAt: new Date().toISOString(),
                })
                await saveOfflineIpqcSession(
                  createOfflineSessionSnapshot({
                    schema,
                    form: currentForm,
                    lineStatus,
                    sessionId,
                  })
                )
              } catch (err) {
                if (!isRecoverableNetworkSubmitError(err)) {
                  throw err
                }
              }
            }
          }
          onStatusChange?.("saved")
        }
      } catch (err) {
        onStatusChange?.("error")
        console.error(err)
      }
    }

    async function flushSyncQueue() {
      if (syncInFlightRef.current) {
        syncQueuedRef.current = true
        return
      }

      syncInFlightRef.current = true
      try {
        await syncDraftToStorage()
      } finally {
        syncInFlightRef.current = false

        if (syncQueuedRef.current) {
          syncQueuedRef.current = false
          void flushSyncQueue()
        }
      }
    }

    const hash = form.addChangeHandler(
      "products",
      () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          void flushSyncQueue()
        }, 500)
      },
      { deep: true }
    )

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      syncQueuedRef.current = false
      form.removeChangeHandler("products", hash)
    }
  }, [form, schema, enabled, isReadOnly, lineStatus, sessionId, onStatusChange])
}

// ============================================================================
// Submit All Sheets - Adapter for new API
// ============================================================================

export interface SubmitAllSheetsParams {
  form: FormData
  schema: FormSchema | HandoverFormSchema
  lineStatus: Record<string, FormParamStatus | undefined>
  sessionId: string
  submittingLines?: Set<string>
}

export interface SubmitAllSheetsResult {
  submittedSheetCount: number
  skippedLocalOnlySheetCount: number
}

async function resolveOfflineIpqcHandoverSyncState(): Promise<{
  handoverSchemas: HandoverFormSchema[]
  handoverForms: FormData[]
  lineStatus: Record<string, FormParamStatus | undefined>
}> {
  const store = useStore.getState()
  const persistedSession = await loadOfflineIpqcSession()

  return {
    handoverSchemas:
      store.ipqc.schemas.handovers.length > 0
        ? store.ipqc.schemas.handovers
        : persistedSession?.handoverSchemas || [],
    handoverForms:
      store.ipqc.forms.handovers.length > 0
        ? store.ipqc.forms.handovers
        : persistedSession?.handoverForms || [],
    lineStatus:
      Object.keys(store.ipqc.lineStatus).length > 0
        ? store.ipqc.lineStatus
        : persistedSession?.lineStatus || {},
  }
}

export function hasDeferredSyncLines(
  schema: FormSchema | HandoverFormSchema
): boolean {
  return schema.products.some((product) =>
    product.productionLines.some((line) => !line.serverSheetId)
  )
}

function resolveServerSheetId(
  lineSchema: ProductionLineSchema | undefined
): string | null {
  if (lineSchema?.serverSheetId) {
    return lineSchema.serverSheetId
  }

  return null
}

function mapLineStatus(status?: FormParamStatus): FormParamStatus {
  if (!status) {
    return FormParamStatus.DRAFT
  }

  if (
    status === FormParamStatus.HANDOVER_SUBMITTED ||
    status === FormParamStatus.SUBMITTED ||
    status === FormParamStatus.COMPLETED
  ) {
    return FormParamStatus.SUBMITTED
  }

  return FormParamStatus.DRAFT
}

function hasActiveBreakdown(breakdowns?: BreakdownFormData[]): boolean {
  return breakdowns?.some((breakdown) => !breakdown.endedAt) ?? false
}

function normalizeReadingPayloadValue(
  value: unknown,
  paramType?: ParamSchemaType
): string | undefined {
  if (!isFilled(value)) {
    return undefined
  }

  if (paramType === ParamSchemaType.VALUE_NUMBER) {
    return String(Number(value))
  }

  return String(value)
}

function getReadingValueForSubmit(
  status: FormParamStatus | undefined,
  value: unknown,
  paramType?: ParamSchemaType,
  options?: { notApplicableWhenEmpty?: boolean }
): string {
  if (
    status === FormParamStatus.NOT_APPLICABLE ||
    (options?.notApplicableWhenEmpty && !isFilled(value))
  ) {
    return FormParamStatus.NOT_APPLICABLE
  }

  return normalizeReadingPayloadValue(value, paramType) ?? ""
}

function buildChildReadingPayload(
  status: FormParamStatus | undefined,
  resample: SingleValueParamData["resample"],
  resampleCleared: boolean | undefined,
  paramType?: ParamSchemaType,
  isFormula?: boolean
): ChildReadingRequest[] | undefined {
  if (resampleCleared === true) {
    return []
  }

  if (status === FormParamStatus.NOT_APPLICABLE) {
    return undefined
  }

  const childValue = normalizeReadingPayloadValue(resample?.value, paramType)
  if (!childValue) {
    return undefined
  }

  const childPayload: ChildReadingRequest = {
    value: childValue,
    remark: resample?.remark,
    recordedAt: resample?.recordedAt,
    gradeLabel: resample?.gradeLabel,
    isFormula: !!isFormula || undefined,
  }

  return [childPayload]
}

function buildChildWindowPayload(
  status: FormParamStatus | undefined,
  resample: WindowParamFormData["window"][number]["resample"],
  resampleCleared: boolean | undefined,
  paramType?: ParamSchemaType,
  isFormula?: boolean
): ChildWindowRequest[] | undefined {
  if (resampleCleared === true) {
    return []
  }

  if (status === FormParamStatus.NOT_APPLICABLE) {
    return undefined
  }

  const childValue = normalizeReadingPayloadValue(resample?.value, paramType)
  if (!childValue) {
    return undefined
  }

  const childPayload: ChildWindowRequest = {
    value: childValue,
    remark: resample?.remark,
    recordedAt: resample?.recordedAt,
    gradeLabel: resample?.gradeLabel,
    isFormula: !!isFormula || undefined,
    overtime: resample?.overtime,
    early: resample?.early,
    sequenceOrder: resample?.sequenceOrder,
  }

  return [childPayload]
}

/**
 * Submit all sheets using the new readings API
 * Groups readings by sheet with explicit status (DRAFT or SUBMITTED)
 */
export async function submitAllSheetsNew(
  params: SubmitAllSheetsParams
): Promise<SubmitAllSheetsResult> {
  const sheets: SheetReadingsRequest[] = []
  let skippedLocalOnlySheetCount = 0

  params.form.products.forEach((product, prodIdx) => {
    const prodSchema = params.schema.products.at(prodIdx)

    product.productionLines.forEach((line, lineIdx) => {
      const lineSchema = prodSchema?.productionLines.at(lineIdx)
      const lineStatusKey = toLineStatusKey({
        productId: prodSchema?.id || "",
        lineId: lineSchema?.id || "",
        sessionId: params.sessionId,
      })
      const isBeingSubmittedNow = params.submittingLines?.has(lineStatusKey)
      const storeStatus = mapLineStatus(params.lineStatus[lineStatusKey])
      const schemaStatus = mapLineStatus(lineSchema?.status)
      const lineHasActiveBreakdown = line.machines.some((machine) =>
        hasActiveBreakdown(machine.breakdown)
      )

      // Skip lines already submitted (unless being submitted now)
      if (
        (storeStatus === FormParamStatus.SUBMITTED ||
          schemaStatus === FormParamStatus.SUBMITTED) &&
        !isBeingSubmittedNow &&
        !lineHasActiveBreakdown
      ) {
        return
      }

      // When manually submitting specific lines, only send those lines
      if (params.submittingLines && !isBeingSubmittedNow) {
        return
      }

      const sheetApiStatus: "DRAFT" | "SUBMITTED" = isBeingSubmittedNow
        ? "SUBMITTED"
        : "DRAFT"

      const sheetId = resolveServerSheetId(lineSchema)
      const sheetReadings: SheetReadingRequest[] = []
      let hasMeaningfulReading = false
      const breakdowns: OfflineIpqcBreakdown[] = []

      line.machines.forEach((machine, machineIdx) => {
        const machineSchema = lineSchema?.machines.at(machineIdx)
        const machineHasActiveBreakdown = hasActiveBreakdown(
          machine.breakdown
        )
        const stageId = machineSchema?.id || machine.id

        ;(machine.breakdown || []).forEach((breakdown) => {
          breakdowns.push({
            stageId,
            ...breakdown,
            id: breakdown.id || createUuid(),
            remark: breakdown.remark || "",
            startedAt: breakdown.startedAt,
            endedAt: breakdown.endedAt || "",
            type: breakdown.type,
          })
        })

        machine.internalProcesses.forEach((proc, procIdx) => {
          const procSchema = machineSchema?.internalProcesses.at(procIdx)

          proc.parameters.forEach((param, paramIdx) => {
            const paramSchema = procSchema?.parameters.at(paramIdx)
            const parameterId = paramSchema?.parameterId

            if (!parameterId) return

            if ("window" in param && param.window?.length) {
              // Windowed parameter
              const windowParam = param as WindowParamFormData
              const windowReadings: WindowReadingRequest[] = []

              windowParam.window?.forEach((window) => {
                if (
                  machineHasActiveBreakdown ||
                  window.status === FormParamStatus.NOT_APPLICABLE ||
                  isFilled(window.value) ||
                  isFilled(window.remark) ||
                  isFilled(window.gradeLabel) ||
                  window.resampleCleared === true ||
                  isFilled(window.resample?.value)
                ) {
                  hasMeaningfulReading = true
                }

                const value = getReadingValueForSubmit(
                  window.status,
                  window.value,
                  paramSchema?.type,
                  { notApplicableWhenEmpty: machineHasActiveBreakdown }
                )
                const childWindows = buildChildWindowPayload(
                  window.status,
                  window.resample,
                  window.resampleCleared,
                  paramSchema?.type,
                  !!paramSchema?.formula
                )

                const readingPayload: WindowReadingRequest = {
                  startTime: window.startTime,
                  endTime: window.endTime,
                  value,
                  recordedAt: window.recordedAt || new Date().toISOString(),
                  remark: window.remark,
                  gradeLabel: window.gradeLabel,
                }

                if (childWindows !== undefined) {
                  readingPayload.childWindows = childWindows
                }

                windowReadings.push(readingPayload)
              })

              if (windowReadings.length) {
                sheetReadings.push({
                  parameterId,
                  windowReadings,
                })
              }
            } else {
              // Single value parameter
              const singleParam = param as SingleValueParamData

              if (
                machineHasActiveBreakdown ||
                singleParam.status === FormParamStatus.NOT_APPLICABLE ||
                isFilled(singleParam.value) ||
                isFilled(singleParam.remark) ||
                isFilled(singleParam.gradeLabel) ||
                singleParam.resampleCleared === true ||
                isFilled(singleParam.resample?.value)
              ) {
                hasMeaningfulReading = true
              }

              const value = getReadingValueForSubmit(
                singleParam.status,
                singleParam.value,
                paramSchema?.type,
                { notApplicableWhenEmpty: machineHasActiveBreakdown }
              )
              const childReadings = buildChildReadingPayload(
                singleParam.status,
                singleParam.resample,
                singleParam.resampleCleared,
                paramSchema?.type,
                !!paramSchema?.formula
              )

              const readingPayload: SheetReadingRequest = {
                parameterId,
                value,
                gradeLabel: singleParam.gradeLabel,
                remark: singleParam.remark,
                recordedAt: singleParam.recordedAt || new Date().toISOString(),
              }

              if (childReadings !== undefined) {
                readingPayload.childReadings = childReadings
              }

              sheetReadings.push(readingPayload)
            }
          })
        })
      })

      if (!sheetId) {
        skippedLocalOnlySheetCount += 1
        return
      }

      // For draft sync, skip completely empty sheets.
      // For explicit submit, always include sheet so backend can update status.
      if (
        (sheetReadings.length > 0 && hasMeaningfulReading) ||
        isBeingSubmittedNow ||
        breakdowns.length > 0
      ) {
        sheets.push({
          sheetId,
          status: sheetApiStatus,
          readings: sheetReadings,
          breakdowns,
        })
      }
    })
  })

  // Submit all sheets if there are any
  if (sheets.length > 0) {
    await submitSheetReadings({ sheets })
  }

  return {
    submittedSheetCount: sheets.length,
    skippedLocalOnlySheetCount,
  }
}

export async function syncSubmittedOfflineIpqcHandovers(): Promise<void> {
  const { handoverSchemas, handoverForms, lineStatus } =
    await resolveOfflineIpqcHandoverSyncState()

  for (const handoverSchema of handoverSchemas) {
    const sessionId = handoverSchema.handoverFromEmployeeSessionId
    if (!sessionId) {
      continue
    }

    const handoverForm = handoverForms.find(
      (form) => form.handoverFromEmployeeSessionId === sessionId
    )
    if (!handoverForm) {
      continue
    }

    await submitAllSheetsNew({
      form: handoverForm,
      schema: handoverSchema,
      lineStatus,
      sessionId,
    })

    const submittingLines = new Set<string>(
      handoverSchema.products.flatMap((product) =>
        product.productionLines.flatMap((line) => {
          const lineStatusKey = toLineStatusKey({
            productId: product.id,
            lineId: line.id,
            sessionId,
          })
          const status = mapLineStatus(lineStatus[lineStatusKey] || line.status)

          return status === FormParamStatus.SUBMITTED ? [lineStatusKey] : []
        })
      )
    )

    if (!submittingLines.size) {
      continue
    }

    await submitAllSheetsNew({
      form: handoverForm,
      schema: handoverSchema,
      lineStatus,
      sessionId,
      submittingLines,
    })
  }
}

// ============================================================================
// Draft Sheets & End Shift Helpers
// ============================================================================

export interface SuggestedEmployee {
  employeeCode: string
  employeeName: string
}

export interface GetDraftSheetsRes {
  products: ProductSchema[]
  handover?: HandoverFormSchema[]
  employees: SuggestedEmployee[]
}

/**
 * Get draft sheets for end shift flow
 * Maps the new session API to the expected format for EndShiftSheet component
 */
export async function getDraftSheetsNew(
  shiftStartTime?: string,
  shiftEndTime?: string
): Promise<GetDraftSheetsRes> {
  const sessionData = await fetchSessionWithSchema(shiftStartTime, shiftEndTime)

  // TODO: Fetch suggested employees from a separate endpoint if available
  // For now, return empty employees array
  return {
    products: sessionData.products,
    handover: sessionData.handovers,
    employees: [],
  }
}

/**
 * End the current IPQC session/shift
 */
export async function endShiftNew(
  handoverEmployeeCode?: string,
  handoverSheetIds?: string[]
): Promise<EndSessionResponse> {
  const store = useStore.getState()

  const request: EndSessionRequest = {}

  if (handoverEmployeeCode) {
    request.handoverEmployeeCode = handoverEmployeeCode
  }

  if (handoverSheetIds?.length) {
    request.handoverSheetIds = handoverSheetIds
  }

  const res = await endSession(request)

  // Only end process locally if backend confirmed success
  if (res.success) {
    store.session.endProcess(store.ipqc.shift.processId)
  }

  return res
}

/**
 * Discard the current IPQC session/shift
 */
export async function discardShiftNew(): Promise<DiscardSessionResponse> {
  const store = useStore.getState()
  const isOnline =
    typeof navigator === "undefined" ? true : navigator.onLine !== false

  if (isOnline) {
    const response = await discardSession()

    if (response.success) {
      store.session.clearPendingIpqcDiscard()
      await clearOfflineIpqcSessionState()
      store.ipqc.clearShift()
    }

    return response
  }

  if (store.ipqc.shift.processId) {
    store.session.setPendingIpqcDiscard(store.ipqc.shift.processId)
  }
  store.ipqc.clearShift()

  return {
    success: true,
    message: "IPQC process discarded locally",
  }
}

export async function markOfflineIpqcPendingFinalize(params?: {
  handoverEmployeeCode?: string
}): Promise<void> {
  const store = useStore.getState()
  if (!store.ipqc.offline.activeSession) {
    return
  }

  store.ipqc.updateOfflineSessionMeta({
    pendingFinalizeAt: new Date().toISOString(),
    handoverEmployeeCode: params?.handoverEmployeeCode || undefined,
    isDirty: true,
  })

  await saveOfflineIpqcSession(createOfflineSessionSnapshot())
}

export async function clearOfflineIpqcSessionState(): Promise<void> {
  const store = useStore.getState()
  store.ipqc.clearFormData()
  store.ipqc.clearOfflineSession()
  store.ipqc.setReadOnlyMode(false)
  await saveOfflineIpqcSession(null)
}

/**
 * Get products available for IPQC (replaces getProductsOfShift)
 */
export async function getIPQCProductsForShift(): Promise<{
  products: Array<{ id: string; name: string }>
}> {
  const store = useStore.getState()
  const bundle = await ensureOfflineBundleAvailable({
    isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  })

  const products = bundle.products.map((p) => ({
    id: p.id,
    name: p.name,
  }))

  store.ipqc.setShiftDetails({
    products,
  })

  return { products }
}

function cloneTemplate<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export const IPQC_OFFLINE_BUNDLE_OFFLINE_MESSAGE =
  "IPQC offline bundle is unavailable. Connect to the internet and retry."
export const IPQC_OFFLINE_BUNDLE_FETCH_FAILED_MESSAGE =
  "Failed to fetch IPQC offline bundle."

type OfflineBundleErrorReason = "offline" | "fetch_failed"

export class IpqcOfflineBundleUnavailableError extends Error {
  readonly code = "IPQC_OFFLINE_BUNDLE_UNAVAILABLE"
  readonly reason: OfflineBundleErrorReason

  constructor(reason: OfflineBundleErrorReason, message: string) {
    super(message)
    this.name = "IpqcOfflineBundleUnavailableError"
    this.reason = reason
  }
}

let preloadOfflineBundlePromise: Promise<OfflineIpqcBundle> | null = null

export function getIpqcOfflineBundleErrorMessage(error: unknown): string {
  if (error instanceof IpqcOfflineBundleUnavailableError) {
    return error.message
  }

  if (error instanceof HTTPError) {
    return error.message || IPQC_OFFLINE_BUNDLE_FETCH_FAILED_MESSAGE
  }

  if (error instanceof Error) {
    return error.message || IPQC_OFFLINE_BUNDLE_FETCH_FAILED_MESSAGE
  }

  return IPQC_OFFLINE_BUNDLE_FETCH_FAILED_MESSAGE
}

function hasHandoverEmployeesInBundle(
  bundle: OfflineIpqcBundle | null | undefined
): bundle is OfflineIpqcBundle {
  return !!bundle && Array.isArray(bundle.employees)
}

export async function preloadOfflineBundle(): Promise<OfflineIpqcBundle> {
  const response = await getOfflineBundle()
  const bundle: OfflineIpqcBundle = {
    version: response.bundleVersion,
    checksum: response.bundleChecksum,
    generatedAt: response.generatedAt,
    context: response.context,
    cachedAt: new Date().toISOString(),
    employees: (response.employees || []).map(
      (employee): OfflineIpqcEmployee => ({
        employeeCode: employee.employeeCode,
        employeeName: employee.employeeName,
      })
    ),
    products: response.products.map(mapOfflineBundleProductToSchema),
  }

  const store = useStore.getState()
  store.ipqc.setOfflineBundle(bundle)
  await saveOfflineIpqcBundle(bundle)

  return bundle
}

export async function ensureOfflineBundleAvailable(params?: {
  isOnline?: boolean
}): Promise<OfflineIpqcBundle> {
  const store = useStore.getState()
  const offlineBundle = store.ipqc.offline.bundle

  if (hasHandoverEmployeesInBundle(offlineBundle)) {
    return offlineBundle
  }

  if (offlineBundle && params?.isOnline === false) {
    return offlineBundle
  }

  if (params?.isOnline === false) {
    throw new IpqcOfflineBundleUnavailableError(
      "offline",
      IPQC_OFFLINE_BUNDLE_OFFLINE_MESSAGE
    )
  }

  if (!preloadOfflineBundlePromise) {
    preloadOfflineBundlePromise = preloadOfflineBundle().finally(() => {
      preloadOfflineBundlePromise = null
    })
  }

  try {
    const bundle = await preloadOfflineBundlePromise
    if (bundle) {
      return bundle
    }
  } catch (error) {
    throw new IpqcOfflineBundleUnavailableError(
      "fetch_failed",
      getIpqcOfflineBundleErrorMessage(error)
    )
  }

  throw new IpqcOfflineBundleUnavailableError(
    "fetch_failed",
    IPQC_OFFLINE_BUNDLE_FETCH_FAILED_MESSAGE
  )
}

export async function ensureOfflineBundleLoaded(): Promise<OfflineIpqcBundle | null> {
  const store = useStore.getState()
  if (store.ipqc.offline.bundle) {
    return store.ipqc.offline.bundle
  }
  return null
}

export async function ensureOfflineBundleCached(): Promise<OfflineIpqcBundle | null> {
  const existing = await ensureOfflineBundleLoaded()
  if (existing) {
    return existing
  }

  try {
    return await ensureOfflineBundleAvailable({
      isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
    })
  } catch {
    return null
  }
}

function buildOfflineSessionMeta(params?: {
  serverSessionId?: string
}): OfflineIpqcSession["meta"] {
  const store = useStore.getState()

  return {
    clientSessionId: createUuid(),
    serverSessionId: params?.serverSessionId,
    shiftId: store.session.shift.id,
    shiftSessionId: store.session.session.id,
    processId: store.ipqc.shift.processId,
    startedAt: new Date().toISOString(),
    employeeCode: store.user.employee.code,
    plantId: store.user.plant.id,
    isDirty: true,
    hasDeferredSync: !params?.serverSessionId,
  }
}

export function hydrateStoreFromOfflineSession(
  session: OfflineIpqcSession
): void {
  const store = useStore.getState()
  store.ipqc.clearFormData()
  store.ipqc.hydrateOfflineSession(normalizeOfflineIpqcSession(session))
  store.ipqc.setReadOnlyMode(false)
}

export function createOfflineSessionSnapshot(params?: {
  schema?: FormSchema | HandoverFormSchema
  form?: FormData
  lineStatus?: Record<string, FormParamStatus | undefined>
  sessionId?: string
}): OfflineIpqcSession {
  const store = useStore.getState()
  const activeSession = store.ipqc.offline.activeSession

  if (!activeSession) {
    throw new Error("No active offline IPQC session found")
  }

  const schema =
    params?.schema && !params.sessionId
      ? (params.schema as FormSchema)
      : store.ipqc.schemas.self
  const form =
    params?.form && !params.sessionId ? params.form : store.ipqc.forms.self
  const dedupedSelfSession = dedupeProductsWithForms({
    products: (schema as FormSchema).products,
    formProducts: form.products,
  })

  return {
    meta: {
      ...cloneTemplate(activeSession),
      employeeCode:
        activeSession.employeeCode || store.user.employee.code || undefined,
      plantId: activeSession.plantId || store.user.plant.id || undefined,
      hasDeferredSync: activeSession.hasDeferredSync || false,
    },
    schema: cloneTemplate({
      ...(schema as FormSchema),
      products: dedupedSelfSession.products,
    }),
    form: cloneTemplate({
      ...form,
      products: dedupedSelfSession.formProducts,
    }),
    handoverSchemas: cloneTemplate(store.ipqc.schemas.handovers),
    handoverForms: cloneTemplate(store.ipqc.forms.handovers),
    lineStatus: cloneTemplate(params?.lineStatus || store.ipqc.lineStatus),
    currProductIdx: store.ipqc.currProductIdx,
    currLineIdx: store.ipqc.currLineIdx,
    currHandoverIdx: store.ipqc.currHandoverIdx,
    currHandoverSessionId: store.ipqc.currHandoverSessionId,
  }
}

export async function startOfflineIpqcSession(
  productIds: string[]
): Promise<OfflineIpqcSession> {
  const store = useStore.getState()
  const bundle = await ensureOfflineBundleAvailable({
    isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  })

  const selectedProducts = bundle.products
    .filter((product) => productIds.includes(product.id))
    .map((product) => cloneTemplate(product))

  if (selectedProducts.length !== productIds.length) {
    throw new Error("One or more selected IPQC products are unavailable")
  }

  const lineStatuses: Record<string, FormParamStatus | undefined> = {}
  selectedProducts.forEach((product) => {
    product.productionLines.forEach((line) => {
      lineStatuses[
        toLineStatusKey({ productId: product.id, lineId: line.id })
      ] = line.status || FormParamStatus.PENDING
    })
  })

  const schema: FormSchema = {
    products: selectedProducts,
  }
  const form = schemaToFormData(schema, lineStatuses, "self")

  const meta = buildOfflineSessionMeta()

  store.ipqc.clearFormData()
  store.ipqc.setSelfSchema(schema)
  store.ipqc.setSelfFormData(form)
  store.ipqc.setLineStatuses(lineStatuses)
  store.ipqc.setCurrProductIdx(0)
  store.ipqc.setCurrLineIdx(0)
  store.ipqc.setCurrHandoverIdx(-1)
  store.ipqc.startOfflineSession(meta)

  return {
    meta,
    schema,
    form,
    handoverSchemas: [],
    handoverForms: [],
    lineStatus: lineStatuses,
    currProductIdx: 0,
    currLineIdx: 0,
    currHandoverIdx: -1,
    currHandoverSessionId: "",
  }
}

async function resolveOfflineIpqcSessionMeta(): Promise<
  OfflineIpqcSession["meta"]
> {
  const store = useStore.getState()
  const activeSession = store.ipqc.offline.activeSession

  if (
    activeSession?.clientSessionId &&
    activeSession.shiftId &&
    activeSession.shiftSessionId &&
    activeSession.processId &&
    activeSession.startedAt
  ) {
    return activeSession
  }

  const persistedSession = await loadOfflineIpqcSession()
  const persistedMeta = persistedSession?.meta

  if (
    persistedMeta?.clientSessionId &&
    persistedMeta.shiftId &&
    persistedMeta.shiftSessionId &&
    persistedMeta.processId &&
    persistedMeta.startedAt
  ) {
    store.ipqc.updateOfflineSessionMeta(persistedMeta)
    return {
      ...(activeSession || ({} as OfflineIpqcSession["meta"])),
      ...persistedMeta,
    }
  }

  throw new Error("No active offline IPQC session found")
}

async function resolvePendingFinalizeOfflineIpqcSessionMeta(): Promise<
  OfflineIpqcSession["meta"] | null
> {
  const store = useStore.getState()
  const activeSession = store.ipqc.offline.activeSession

  if (activeSession?.pendingFinalizeAt) {
    return activeSession
  }

  const persistedSession = await loadOfflineIpqcSession()
  const persistedMeta = persistedSession?.meta

  if (!persistedMeta?.pendingFinalizeAt) {
    return null
  }

  store.ipqc.updateOfflineSessionMeta(persistedMeta)

  return {
    ...(activeSession || ({} as OfflineIpqcSession["meta"])),
    ...persistedMeta,
  }
}

async function resolveOfflineIpqcSyncSource(): Promise<{
  meta: OfflineIpqcSession["meta"]
  schema: FormSchema
  form: FormData
  lineStatus: Record<string, FormParamStatus | undefined>
}> {
  const store = useStore.getState()
  const meta = await resolveOfflineIpqcSessionMeta()
  const persistedSession = await loadOfflineIpqcSession()
  const hasInMemorySession =
    store.ipqc.schemas.self.products.length > 0 &&
    store.ipqc.forms.self.products.length > 0

  const schema = hasInMemorySession
    ? store.ipqc.schemas.self
    : persistedSession?.schema
  const form = hasInMemorySession ? store.ipqc.forms.self : persistedSession?.form
  const lineStatus =
    Object.keys(store.ipqc.lineStatus).length > 0
      ? store.ipqc.lineStatus
      : persistedSession?.lineStatus

  if (!schema || !form || !lineStatus) {
    throw new Error("No offline IPQC session data found")
  }

  return {
    meta,
    schema,
    form,
    lineStatus,
  }
}

async function persistOfflineIpqcSessionSnapshot(): Promise<void> {
  const store = useStore.getState()
  const hasInMemorySession =
    !!store.ipqc.offline.activeSession &&
    store.ipqc.schemas.self.products.length > 0 &&
    store.ipqc.forms.self.products.length > 0

  if (hasInMemorySession) {
    await saveOfflineIpqcSession(createOfflineSessionSnapshot())
    return
  }

  const persistedSession = await loadOfflineIpqcSession()

  if (persistedSession && store.ipqc.offline.activeSession) {
    await saveOfflineIpqcSession({
      ...persistedSession,
      meta: {
        ...persistedSession.meta,
        ...store.ipqc.offline.activeSession,
      },
    })
  }
}

export async function buildOfflineSyncPayload(params?: {
  handoverEmployeeCode?: string
  finalize?: boolean
}): Promise<OfflineSyncRequest> {
  const store = useStore.getState()
  const syncSource = await resolveOfflineIpqcSyncSource()
  const activeSession = syncSource.meta

  const sheets: OfflineSyncSheet[] = syncSource.schema.products.flatMap(
    (product, productIdx) => {
      const formProduct = syncSource.form.products[productIdx]

      return product.productionLines.map((line, lineIdx) => {
        const formLine = formProduct?.productionLines[lineIdx]
        const sheetStatus = mapLineStatus(
          syncSource.lineStatus[
            toLineStatusKey({ productId: product.id, lineId: line.id })
          ]
        )

        const readings: SheetReadingRequest[] = []

        formLine?.machines.forEach((machine, machineIdx) => {
          const schemaMachine = line.machines[machineIdx]
          const machineHasActiveBreakdown = hasActiveBreakdown(
            machine.breakdown
          )

          machine.internalProcesses.forEach((proc, procIdx) => {
            const schemaProc = schemaMachine?.internalProcesses[procIdx]

            proc.parameters.forEach((param, paramIdx) => {
              const schemaParam = schemaProc?.parameters[paramIdx]
              const parameterId = schemaParam?.parameterId

              if (!parameterId) {
                return
              }

              if ("window" in param && param.window?.length) {
                readings.push({
                  parameterId,
                  windowReadings: param.window.map((window) => {
                    const childWindows = buildChildWindowPayload(
                      window.status,
                      window.resample,
                      window.resampleCleared,
                      schemaParam?.type,
                      !!schemaParam?.formula
                    )
                    const readingPayload: WindowReadingRequest = {
                      startTime: window.startTime,
                      endTime: window.endTime,
                      value: getReadingValueForSubmit(
                        window.status,
                        window.value,
                        schemaParam?.type,
                        { notApplicableWhenEmpty: machineHasActiveBreakdown }
                      ),
                      recordedAt:
                        window.recordedAt || new Date().toISOString(),
                      gradeLabel: window.gradeLabel,
                      remark: window.remark,
                    }

                    if (childWindows !== undefined) {
                      readingPayload.childWindows = childWindows
                    }

                    return readingPayload
                  }),
                })
              } else {
                const singleParam = param as SingleValueParamData
                const childReadings = buildChildReadingPayload(
                  singleParam.status,
                  singleParam.resample,
                  singleParam.resampleCleared,
                  schemaParam?.type,
                  !!schemaParam?.formula
                )
                const readingPayload: SheetReadingRequest = {
                  parameterId,
                  value: getReadingValueForSubmit(
                    singleParam.status,
                    singleParam.value,
                    schemaParam?.type,
                    { notApplicableWhenEmpty: machineHasActiveBreakdown }
                  ),
                  recordedAt:
                    singleParam.recordedAt || new Date().toISOString(),
                  gradeLabel: singleParam.gradeLabel,
                  remark: singleParam.remark,
                }

                if (childReadings !== undefined) {
                  readingPayload.childReadings = childReadings
                }

                readings.push(readingPayload)
              }
            })
          })
        })

        const breakdowns =
          formLine?.machines.flatMap((machine, machineIdx) => {
            const stageId = line.machines[machineIdx]?.id || machine.id
            return (machine.breakdown || []).map((breakdown) => ({
              stageId,
              ...breakdown,
              id: breakdown.id || createUuid(),
              remark: breakdown.remark || "",
              startedAt: breakdown.startedAt,
              endedAt: breakdown.endedAt,
              type: breakdown.type,
            }))
          }) || []

        return {
          lineId: line.id,
          productId: product.id,
          name: line.name,
          status:
            params?.finalize || sheetStatus === FormParamStatus.SUBMITTED
              ? "SUBMITTED"
              : "DRAFT",
          readings,
          breakdowns,
        }
      })
    }
  )

  const dedupedSheets = dedupeOfflineSyncSheets(sheets)

  return {
    shiftId: store.session.shift.id,
    shiftSessionId: store.session.session.id,
    processId: store.ipqc.shift.processId,
    clientSessionId: activeSession.clientSessionId,
    startedAt: activeSession.startedAt,
    selectedProducts: Array.from(
      new Set(syncSource.schema.products.map((product) => product.id))
    ),
    sheets: dedupedSheets,
    handoverEmployeeCode:
      params?.handoverEmployeeCode || activeSession.handoverEmployeeCode,
  }
}

export async function syncCurrentOfflineIpqcSession(params?: {
  handoverEmployeeCode?: string
  finalize?: boolean
}): Promise<OfflineSyncResponse> {
  const payload = await buildOfflineSyncPayload(params)
  const response = await syncOfflineSession(payload)
  const store = useStore.getState()
  const shiftStartTime = store.session.shift.startedAt || undefined
  const shiftEndTime = store.session.shift.endedAt || undefined

  store.ipqc.updateOfflineSessionMeta({
    serverSessionId: response.sessionId,
    isDirty: false,
    hasDeferredSync: false,
    syncedAt: new Date().toISOString(),
  })

  const hydratedSession = await hydrateOnlineIpqcSession(
    shiftStartTime,
    shiftEndTime
  )

  if (!hydratedSession?.sessionId) {
    await persistOfflineIpqcSessionSnapshot()
  }

  return response
}

export async function finalizePendingOfflineIpqcProcess(): Promise<EndSessionResponse | null> {
  const store = useStore.getState()
  const isOnline =
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  const activeSession = await resolvePendingFinalizeOfflineIpqcSessionMeta()

  if (!activeSession?.pendingFinalizeAt) {
    return null
  }

  if (!isOnline) {
    throw new Error(
      "Reconnect to the internet before syncing the offline IPQC process."
    )
  }

  store.ipqc.setShiftDetails({
    id: activeSession.shiftId,
    processId: activeSession.processId,
    shiftId: activeSession.shiftId,
  })

  const syncResponse = await syncCurrentOfflineIpqcSession({
    finalize: true,
    handoverEmployeeCode: activeSession.handoverEmployeeCode,
  })

  if (!syncResponse.success) {
    throw new Error("Failed to sync offline IPQC process")
  }

  await syncSubmittedOfflineIpqcHandovers()

  const handoverCheck = await checkHandover()
  const endResponse = await endShiftNew(
    activeSession.handoverEmployeeCode,
    handoverCheck.pendingSheetIds
  )

  if (!endResponse.success) {
    throw new Error(
      endResponse.message || "Failed to finalize offline IPQC process"
    )
  }

  await clearOfflineIpqcSessionState()

  return endResponse
}
